import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Divider } from "@/components/Divider";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email, and message are required.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll respond within one business day.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Couldn't send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white" data-testid="page-contact">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#00A651] mb-4">Get In Touch</p>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl text-black tracking-tight max-w-3xl">
          Real people. Real answers. Real fast.
        </h1>
        <Divider className="my-6" />
        <p className="font-sub italic text-lg text-gray-600 max-w-2xl">
          Have a question about a program, your eligibility, or your current loan?
          Send a note or call directly — your message lands with a licensed originator.
        </p>

        <div className="grid lg:grid-cols-12 gap-10 mt-12">
          {/* FORM */}
          <form
            onSubmit={submit}
            className="lg:col-span-7 border border-gray-200 p-6 sm:p-10 rounded-sm bg-white"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-black">Name</Label>
                <Input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-2 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-black">Email</Label>
                <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-black">Phone</Label>
                <Input data-testid="contact-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-black">Subject</Label>
                <Input data-testid="contact-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className="mt-2 rounded-sm" />
              </div>
            </div>
            <div className="mt-5">
              <Label className="text-xs font-bold uppercase tracking-wider text-black">Message</Label>
              <Textarea
                data-testid="contact-message"
                rows={6}
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                className="mt-2 rounded-sm"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit"
              className="mt-7 bg-[#00A651] hover:bg-[#008f45] disabled:opacity-50 text-white font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-sm transition-colors"
            >
              {sending ? "Sending…" : "Send Message"}
            </button>
          </form>

          {/* INFO */}
          <aside className="lg:col-span-5 space-y-6">
            <div className="bg-black text-white p-8 rounded-sm">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E88C2F]">Office</p>
              <h3 className="font-heading font-bold text-2xl mt-2">DW Investment Group</h3>
              <p className="font-sub italic text-gray-400 mt-1">Insuring Your Future…Today</p>
              <ul className="mt-6 space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-[#00A651] mt-0.5" />
                  <span>123 Mortgage Way, Suite 400<br/>Your City, ST 00000</span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone size={18} className="text-[#00A651]" />
                  <a href="tel:+15551234567" className="hover:text-[#E88C2F]">(555) 123-4567</a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail size={18} className="text-[#00A651]" />
                  <a href="mailto:weithers@dwinvestmentgroup.com" className="hover:text-[#E88C2F] break-all">
                    weithers@dwinvestmentgroup.com
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Clock size={18} className="text-[#00A651]" />
                  <span>Mon – Fri · 9:00 AM – 6:00 PM</span>
                </li>
              </ul>
            </div>
            <div className="aspect-[4/3] bg-gray-200 border border-gray-200 rounded-sm overflow-hidden">
              <iframe
                title="Office Location"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-77.05%2C38.88%2C-77.0%2C38.92&layer=mapnik"
                className="w-full h-full"
                style={{ border: 0 }}
                loading="lazy"
              />
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
